import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  BookOpen,
  Bot,
  Braces,
  Check,
  ChevronRight,
  CircleAlert,
  Clipboard,
  Code2,
  Download,
  Eye,
  EyeOff,
  FileJson,
  FileSearch,
  FileText,
  Gauge,
  GitCompareArrows,
  History,
  KeyRound,
  ListFilter,
  LoaderCircle,
  Menu,
  Moon,
  Network,
  PanelLeftClose,
  RefreshCw,
  Search,
  Shield,
  ShieldCheck,
  Sparkles,
  Sun,
  TableProperties,
  Tags,
  Trash2,
  UploadCloud,
  WandSparkles,
  X,
} from "lucide-react";
import demoJson from "../sample-gtm-container.json?raw";
import {
  analyseContainer,
  buildAiAuditPayload,
  casingLabel,
  countSeverities,
  displaySeparator,
  parseContainerText,
  parseDataLayerText,
} from "./lib/auditEngine";
import {
  AI_PROVIDERS,
  DEFAULT_PROVIDER,
  fetchProviderModels,
  getProvider,
  providerSessionKey,
  runAiAudit,
  runAiFollowUp,
  validateProviderKey,
} from "./lib/aiProviders";
import {
  backendConfigured,
  checkBackend,
  deleteAuditHistory,
  listAuditHistory,
  saveAuditHistory,
} from "./lib/backend";
import {
  DEFAULT_POLICY,
  buildGovernanceInsights,
  compareContainers,
  createAuditSnapshot,
} from "./lib/advancedAudit";
import { exportAuditJson, exportFindingsCsv, exportWordReport } from "./lib/exporters";
import {
  CardHeader,
  FindingsTable,
  MetricCard,
  PageHeading,
  RatingCard,
  StatusChip,
  WelcomeEmpty,
} from "./components/AuditUI";
import {
  ComparePage,
  GovernancePage,
  HistoryPage,
} from "./components/AdvancedPages";
import { GuidePage } from "./components/GuidePage";

const NAV_ITEMS = [
  { id: "overview", label: "Overview", icon: Activity },
  { id: "guide", label: "How to use", icon: BookOpen },
  { id: "naming", label: "Naming", icon: Tags },
  { id: "events", label: "Events", icon: Network },
  { id: "governance", label: "Governance", icon: Gauge },
  { id: "compare", label: "Compare", icon: GitCompareArrows },
  { id: "explorer", label: "Explorer", icon: TableProperties },
  { id: "ai", label: "AI Review", icon: Bot },
  { id: "history", label: "History", icon: History },
  { id: "report", label: "Report", icon: FileText },
];

const MANUAL_DEMO = [
  {
    event: "purchase",
    ecommerce: {
      transaction_id: "T-1001",
      value: "149.50",
      currency: "usd",
      coupon: "WELCOME10",
      items: [
        { item_name: "Audit Plan", price: "149.50", quantity: 1 },
        { item_id: "ADD-ON-01", price: 25, quantity: "1" },
      ],
    },
  },
];

function App() {
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [theme, setTheme] = useState(() => safeLocalGet("gtm-atlas-theme") || "light");
  const [container, setContainer] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [sourceName, setSourceName] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [importError, setImportError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [namingDraft, setNamingDraft] = useState(null);
  const [includePaused, setIncludePaused] = useState(true);
  const [manualText, setManualText] = useState("");
  const [manualEvents, setManualEvents] = useState([]);
  const [manualError, setManualError] = useState("");
  const [inventoryQuery, setInventoryQuery] = useState("");
  const [inventoryType, setInventoryType] = useState("all");
  const [inventoryStatus, setInventoryStatus] = useState("all");
  const [provider, setProvider] = useState(DEFAULT_PROVIDER);
  const [providerKeys, setProviderKeys] = useState(loadSessionProviderKeys);
  const [showKey, setShowKey] = useState(false);
  const [rememberKey, setRememberKey] = useState(
    Boolean(safeSessionGet(providerSessionKey(DEFAULT_PROVIDER))),
  );
  const [keyStatus, setKeyStatus] = useState("idle");
  const [keyMeta, setKeyMeta] = useState(null);
  const [keyError, setKeyError] = useState("");
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(getProvider(DEFAULT_PROVIDER).defaultModel);
  const [freeModelsOnly, setFreeModelsOnly] = useState(true);
  const [aiConsent, setAiConsent] = useState(false);
  const [aiStatus, setAiStatus] = useState("idle");
  const [aiError, setAiError] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiConversation, setAiConversation] = useState([]);
  const [followUpStatus, setFollowUpStatus] = useState("idle");
  const [followUpError, setFollowUpError] = useState("");
  const [policy, setPolicyState] = useState(() =>
    normalizePolicy({
      ...DEFAULT_POLICY,
      ...safeJsonLocalGet("gtm-atlas-policy"),
    }),
  );
  const [baselineContainer, setBaselineContainer] = useState(null);
  const [baselineAnalysis, setBaselineAnalysis] = useState(null);
  const [baselineName, setBaselineName] = useState("");
  const [baselineError, setBaselineError] = useState("");
  const [backendStatus, setBackendStatus] = useState(
    backendConfigured() ? "checking" : "not-configured",
  );
  const [backendFeatures, setBackendFeatures] = useState({});
  const [history, setHistory] = useState([]);
  const [historyStatus, setHistoryStatus] = useState("idle");
  const [historyError, setHistoryError] = useState("");
  const [workspaceToken] = useState(getOrCreateWorkspaceToken);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);
  const aiAbort = useRef(null);
  const fileInput = useRef(null);
  const apiKey = providerKeys[provider] || "";
  const providerConfig = getProvider(provider);

  const currentNav = NAV_ITEMS.find((item) => item.id === activeTab);
  const currentFindings = analysis?.findings || [];
  const governance = useMemo(
    () => buildGovernanceInsights(container, analysis, policy),
    [analysis, container, policy],
  );
  const currentVersionAnalysis = useMemo(
    () =>
      container
        ? analyseContainer(container, {
            namingRules: analysis?.namingRules,
            includePaused,
          })
        : null,
    [analysis?.namingRules, container, includePaused],
  );
  const comparison = useMemo(
    () =>
      compareContainers(
        container,
        baselineContainer,
        currentVersionAnalysis,
        baselineAnalysis,
      ),
    [baselineAnalysis, baselineContainer, container, currentVersionAnalysis],
  );

  const filteredInventory = useMemo(() => {
    if (!analysis) return [];
    const query = inventoryQuery.trim().toLowerCase();
    return analysis.inventory.filter((item) => {
      if (inventoryType !== "all" && item.objectType.toLowerCase() !== inventoryType) return false;
      if (inventoryStatus !== "all" && item.status.toLowerCase() !== inventoryStatus) return false;
      if (!query) return true;
      return [item.name, item.subtype, item.id, item.objectType]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [analysis, inventoryQuery, inventoryStatus, inventoryType]);

  useEffect(() => {
    if (!backendConfigured()) return undefined;
    const controller = new AbortController();
    checkBackend(controller.signal)
      .then((result) => {
        setBackendStatus("online");
        setBackendFeatures(result.features || {});
      })
      .catch(() => {
        setBackendStatus("offline");
        setBackendFeatures({});
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (
      activeTab !== "history" ||
      backendStatus !== "online" ||
      !backendFeatures.auditHistory ||
      historyStatus !== "idle" ||
      history.length
    ) {
      return;
    }
    refreshHistory();
  }, [activeTab, backendFeatures.auditHistory, backendStatus]);

  function notify(message) {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 3200);
  }

  function setApiKey(value) {
    setProviderKeys((current) => ({ ...current, [provider]: value }));
  }

  function selectAiProvider(nextProvider) {
    if (nextProvider === provider) return;
    const nextConfig = getProvider(nextProvider);
    setProvider(nextProvider);
    setKeyStatus("idle");
    setKeyMeta(null);
    setKeyError("");
    setModels([]);
    setModel(nextConfig.defaultModel);
    setFreeModelsOnly(nextConfig.supportsFreeModels);
    setRememberKey(Boolean(safeSessionGet(providerSessionKey(nextProvider))));
    setShowKey(false);
    setAiResult(null);
    setAiConversation([]);
    setAiError("");
    setAiConsent(false);
  }

  function switchTab(tab) {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleTheme() {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    safeLocalSet("gtm-atlas-theme", next);
  }

  function setPolicy(next) {
    const normalized = normalizePolicy(next);
    setPolicyState(normalized);
    safeLocalSet("gtm-atlas-policy", JSON.stringify(normalized));
  }

  function resetPolicy() {
    setPolicy(DEFAULT_POLICY);
    notify("Governance policy reset to the standard profile.");
  }

  function importContainer(text, filename) {
    setImportError("");
    try {
      const parsed = parseContainerText(text);
      const next = analyseContainer(parsed, { manualEvents, includePaused });
      setContainer(parsed);
      setSourceName(filename);
      setAnalysis(next);
      setNamingDraft(cloneRules(next.inferredRules));
      setJsonText(text);
      setAiResult(null);
      setAiError("");
      setAiConversation([]);
      setAiQuestion("");
      notify(
        `Analyzed ${next.counts.tags} tags, ${next.counts.triggers} triggers, and ${next.counts.variables} variables.`,
      );
      return true;
    } catch (error) {
      setImportError(error.message);
      return false;
    }
  }

  function handleFile(file) {
    setImportError("");
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json") && file.type !== "application/json") {
      setImportError("Choose a JSON file exported from Google Tag Manager.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setImportError("This file is larger than the 15 MB browser limit.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => importContainer(String(reader.result), file.name);
    reader.onerror = () => setImportError("The selected file could not be read.");
    reader.readAsText(file);
  }

  function loadDemo() {
    setShowPaste(false);
    importContainer(demoJson, "sample-gtm-container.json");
  }

  function clearWorkspace() {
    if (container && !window.confirm("Clear the container, audit findings, and AI review?")) return;
    setContainer(null);
    setAnalysis(null);
    setSourceName("");
    setJsonText("");
    setImportError("");
    setNamingDraft(null);
    setManualEvents([]);
    setManualText("");
    setManualError("");
    setAiResult(null);
    setAiError("");
    setAiConversation([]);
    setAiQuestion("");
    setBaselineContainer(null);
    setBaselineAnalysis(null);
    setBaselineName("");
    setBaselineError("");
    switchTab("overview");
    notify("Workspace cleared.");
  }

  function applyNamingRules() {
    if (!container || !namingDraft) return;
    const cleaned = Object.fromEntries(
      Object.entries(namingDraft).map(([type, rule]) => [
        type,
        {
          ...rule,
          prefixes: rule.prefixes
            .map((prefix) => prefix.trim())
            .filter(Boolean)
            .slice(0, 12),
          source: "reviewed",
        },
      ]),
    );
    const next = analyseContainer(container, {
      namingRules: cleaned,
      manualEvents,
      includePaused,
    });
    setNamingDraft(cloneRules(cleaned));
    setAnalysis(next);
    notify(`Naming rules applied. New naming score: ${next.ratings.naming.score}/100.`);
  }

  function restoreInferredRules() {
    if (!analysis || !container) return;
    const inferred = cloneRules(analysis.inferredRules);
    setNamingDraft(inferred);
    const next = analyseContainer(container, {
      namingRules: inferred,
      manualEvents,
      includePaused,
    });
    setAnalysis(next);
    notify("Restored automatically inferred naming rules.");
  }

  function validateManualEvents() {
    setManualError("");
    try {
      const events = parseDataLayerText(manualText);
      setManualEvents(events);
      if (container) {
        setAnalysis(
          analyseContainer(container, {
            namingRules: analysis.namingRules,
            manualEvents: events,
            includePaused,
          }),
        );
      }
      notify(`Validated ${events.length} dataLayer event${events.length === 1 ? "" : "s"}.`);
    } catch (error) {
      setManualError(error.message);
    }
  }

  function clearManualEvents() {
    setManualEvents([]);
    setManualText("");
    setManualError("");
    if (container) {
      setAnalysis(
        analyseContainer(container, {
          namingRules: analysis.namingRules,
          manualEvents: [],
          includePaused,
        }),
      );
    }
  }

  async function connectProvider() {
    setKeyStatus("loading");
    setKeyError("");
    const controller = new AbortController();
    try {
      const metadata = await validateProviderKey(provider, apiKey, controller.signal);
      setKeyMeta(metadata);
      setKeyStatus("connected");
      const storageKey = providerSessionKey(provider);
      if (rememberKey) safeSessionSet(storageKey, apiKey.trim());
      else safeSessionRemove(storageKey);
      const items = await fetchProviderModels(provider, apiKey, controller.signal);
      setModels(items.slice(0, 500));
      setModel((current) => chooseProviderModel(items, current, providerConfig, freeModelsOnly));
      notify(`${providerConfig.shortName} key connected. ${items.length} compatible models loaded.`);
    } catch (error) {
      setKeyStatus("error");
      setKeyMeta(null);
      setKeyError(error.message);
      safeSessionRemove(providerSessionKey(provider));
    }
  }

  async function refreshProviderModels() {
    if (keyStatus !== "connected") return;
    setKeyError("");
    try {
      const items = await fetchProviderModels(provider, apiKey);
      setModels(items.slice(0, 500));
      setModel((current) => chooseProviderModel(items, current, providerConfig, freeModelsOnly));
      notify(`Refreshed ${items.length} ${providerConfig.shortName} models.`);
    } catch (error) {
      setKeyError(error.message);
    }
  }

  function disconnectProvider() {
    setProviderKeys((current) => ({ ...current, [provider]: "" }));
    setKeyMeta(null);
    setKeyStatus("idle");
    setKeyError("");
    setModels([]);
    setModel(providerConfig.defaultModel);
    setAiResult(null);
    safeSessionRemove(providerSessionKey(provider));
    notify(`${providerConfig.shortName} key removed from this browser session.`);
  }

  async function runAiReview() {
    if (!container || !analysis) return;
    if (!aiConsent) {
      setAiError("Confirm the sanitized-data notice before running the AI review.");
      return;
    }
    if (keyStatus !== "connected") {
      setAiError(`Connect a valid ${providerConfig.shortName} key first.`);
      return;
    }
    setAiStatus("loading");
    setAiError("");
    aiAbort.current?.abort();
    aiAbort.current = new AbortController();
    try {
      const result = await runAiAudit({
        provider,
        apiKey,
        model,
        auditPayload: buildAiAuditPayload(container, analysis),
        signal: aiAbort.current.signal,
      });
      setAiResult(result);
      setAiConversation([]);
      setAiQuestion("");
      setAiStatus("complete");
      notify("AI review complete.");
    } catch (error) {
      if (error.name === "AbortError") {
        setAiStatus("idle");
        notify("AI review cancelled.");
      } else {
        setAiStatus("error");
        setAiError(error.message);
      }
    }
  }

  function cancelAiReview() {
    aiAbort.current?.abort();
  }

  async function runAiFollowUp() {
    if (!container || !analysis || !aiResult?.review) return;
    const question = aiQuestion.trim();
    if (!question) {
      setFollowUpError("Enter a question about this audit.");
      return;
    }
    setFollowUpStatus("loading");
    setFollowUpError("");
    aiAbort.current?.abort();
    aiAbort.current = new AbortController();
    try {
      const result = await runAiFollowUp({
        provider,
        apiKey,
        model,
        auditPayload: buildAiAuditPayload(container, analysis),
        priorReview: aiResult.review,
        conversation: aiConversation,
        question,
        signal: aiAbort.current.signal,
      });
      setAiConversation((current) => [
        ...current,
        { role: "user", content: question },
        { role: "assistant", content: result.review.answer, detail: result.review },
      ].slice(-8));
      setAiQuestion("");
      setFollowUpStatus("complete");
      notify("AI follow-up complete.");
    } catch (error) {
      if (error.name === "AbortError") {
        setFollowUpStatus("idle");
      } else {
        setFollowUpStatus("error");
        setFollowUpError(error.message);
      }
    }
  }

  function handleBaselineFile(file) {
    setBaselineError("");
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json") && file.type !== "application/json") {
      setBaselineError("Choose a JSON file exported from Google Tag Manager.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setBaselineError("The baseline file exceeds the 15 MB browser limit.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseContainerText(String(reader.result));
        setBaselineContainer(parsed);
        setBaselineAnalysis(analyseContainer(parsed));
        setBaselineName(file.name);
        notify("Baseline parsed. The change ledger is ready.");
      } catch (error) {
        setBaselineError(error.message);
      }
    };
    reader.onerror = () => setBaselineError("The baseline file could not be read.");
    reader.readAsText(file);
  }

  function clearBaseline() {
    setBaselineContainer(null);
    setBaselineAnalysis(null);
    setBaselineName("");
    setBaselineError("");
    notify("Baseline removed.");
  }

  async function refreshHistory() {
    if (backendStatus !== "online" || !backendFeatures.auditHistory) return;
    setHistoryStatus("loading");
    setHistoryError("");
    try {
      const items = await listAuditHistory(workspaceToken);
      setHistory(items);
      setHistoryStatus("idle");
    } catch (error) {
      setHistoryStatus("error");
      setHistoryError(error.message);
    }
  }

  async function saveCurrentAudit() {
    if (!container || !analysis || !governance) return;
    setHistoryStatus("saving");
    setHistoryError("");
    try {
      await saveAuditHistory(
        createAuditSnapshot({ container, analysis, sourceName, governance }),
        workspaceToken,
      );
      await refreshHistory();
      notify("Sanitized audit snapshot saved.");
    } catch (error) {
      setHistoryStatus("error");
      setHistoryError(error.message);
    }
  }

  async function deleteSavedAudit(id) {
    if (!window.confirm("Delete this saved audit snapshot?")) return;
    setHistoryError("");
    try {
      await deleteAuditHistory(id, workspaceToken);
      setHistory((current) => current.filter((item) => item.id !== id));
      notify("Saved audit deleted.");
    } catch (error) {
      setHistoryError(error.message);
    }
  }

  function copyAiReview() {
    if (!aiResult?.review) return;
    navigator.clipboard
      .writeText(JSON.stringify(aiResult.review, null, 2))
      .then(() => notify("AI review copied to clipboard."))
      .catch(() => notify("Copy failed. Select the text manually."));
  }

  function runExport(kind) {
    if (!container || !analysis) return;
    const args = {
      container,
      analysis,
      aiResult,
      sourceName,
      governance,
      comparison,
    };
    if (kind === "docx") exportWordReport(args);
    if (kind === "json") exportAuditJson(args);
    if (kind === "csv") exportFindingsCsv(args);
    notify(`${kind.toUpperCase()} report generated.`);
  }

  return (
    <div className={`app theme-${theme} ${sidebarOpen ? "" : "sidebar-collapsed"}`}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <header className="topbar">
        <button className="brand" type="button" onClick={() => switchTab("overview")}>
          <span className="brand-mark">{"{ }"}</span>
          <span>
            <strong>GTM Audit Atlas</strong>
            <small>React · Cloudflare · AI</small>
          </span>
        </button>

        <div className="breadcrumb">
          <span>Workspace</span>
          <ChevronRight aria-hidden="true" />
          <strong>{currentNav?.label}</strong>
          {container ? (
            <>
              <span className="crumb-divider">/</span>
              <span className="source-name">{sourceName}</span>
            </>
          ) : null}
        </div>

        <div className="topbar-actions">
          <button className="top-action" type="button" onClick={loadDemo}>
            <Braces aria-hidden="true" />
            <span>Demo</span>
          </button>
          <button className="top-action" type="button" onClick={clearWorkspace}>
            <Trash2 aria-hidden="true" />
            <span>Reset</span>
          </button>
          <button
            className="icon-button"
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
          >
            {theme === "light" ? <Moon aria-hidden="true" /> : <Sun aria-hidden="true" />}
          </button>
          <button
            className="primary-button header-export"
            type="button"
            disabled={!analysis}
            onClick={() => runExport("docx")}
          >
            <Download aria-hidden="true" />
            Export report
          </button>
        </div>
      </header>

      <aside className="sidebar" aria-label="Audit sections">
        <button
          type="button"
          className="collapse-button"
          onClick={() => setSidebarOpen((value) => !value)}
          aria-label={sidebarOpen ? "Collapse navigation" : "Expand navigation"}
        >
          {sidebarOpen ? <PanelLeftClose aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
        <nav>
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={activeTab === id ? "active" : ""}
              aria-current={activeTab === id ? "page" : undefined}
              onClick={() => switchTab(id)}
              title={`Open ${label}`}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
              {id === "ai" && aiResult ? <i aria-label="AI review available" /> : null}
            </button>
          ))}
        </nav>
        <div className="sidebar-privacy">
          <ShieldCheck aria-hidden="true" />
          <span>
            <strong>Local first</strong>
            <small>AI is optional</small>
          </span>
        </div>
      </aside>

      <main id="main-content">
        {activeTab === "overview" ? (
          <OverviewPage
            container={container}
            analysis={analysis}
            sourceName={sourceName}
            jsonText={jsonText}
            setJsonText={setJsonText}
            showPaste={showPaste}
            setShowPaste={setShowPaste}
            importError={importError}
            importContainer={importContainer}
            handleFile={handleFile}
            loadDemo={loadDemo}
            dragging={dragging}
            setDragging={setDragging}
            fileInput={fileInput}
            switchTab={switchTab}
            backendStatus={backendStatus}
          />
        ) : null}

        {activeTab === "guide" ? (
          <GuidePage
            switchTab={switchTab}
            backendStatus={backendStatus}
            loadDemo={loadDemo}
          />
        ) : null}

        {activeTab === "naming" ? (
          <NamingPage
            container={container}
            analysis={analysis}
            draft={namingDraft}
            setDraft={setNamingDraft}
            includePaused={includePaused}
            setIncludePaused={setIncludePaused}
            applyRules={applyNamingRules}
            restoreRules={restoreInferredRules}
            loadDemo={loadDemo}
          />
        ) : null}

        {activeTab === "events" ? (
          <EventsPage
            container={container}
            analysis={analysis}
            manualText={manualText}
            setManualText={setManualText}
            manualEvents={manualEvents}
            manualError={manualError}
            validateManualEvents={validateManualEvents}
            clearManualEvents={clearManualEvents}
            useManualDemo={() => setManualText(JSON.stringify(MANUAL_DEMO, null, 2))}
            loadDemo={loadDemo}
          />
        ) : null}

        {activeTab === "governance" ? (
          <GovernancePage
            container={container}
            analysis={analysis}
            governance={governance}
            policy={policy}
            setPolicy={setPolicy}
            resetPolicy={resetPolicy}
            loadDemo={loadDemo}
          />
        ) : null}

        {activeTab === "compare" ? (
          <ComparePage
            container={container}
            baseline={baselineContainer}
            baselineName={baselineName}
            baselineError={baselineError}
            comparison={comparison}
            onBaselineFile={handleBaselineFile}
            clearBaseline={clearBaseline}
            loadDemo={loadDemo}
          />
        ) : null}

        {activeTab === "explorer" ? (
          <ExplorerPage
            container={container}
            analysis={analysis}
            items={filteredInventory}
            query={inventoryQuery}
            setQuery={setInventoryQuery}
            type={inventoryType}
            setType={setInventoryType}
            status={inventoryStatus}
            setStatus={setInventoryStatus}
            loadDemo={loadDemo}
          />
        ) : null}

        {activeTab === "ai" ? (
          <AiPage
            container={container}
            analysis={analysis}
            provider={provider}
            providerConfig={providerConfig}
            providers={AI_PROVIDERS}
            setProvider={selectAiProvider}
            apiKey={apiKey}
            setApiKey={setApiKey}
            showKey={showKey}
            setShowKey={setShowKey}
            rememberKey={rememberKey}
            setRememberKey={setRememberKey}
            keyStatus={keyStatus}
            keyMeta={keyMeta}
            keyError={keyError}
            connect={connectProvider}
            disconnect={disconnectProvider}
            refreshModels={refreshProviderModels}
            models={models}
            model={model}
            setModel={setModel}
            freeModelsOnly={freeModelsOnly}
            setFreeModelsOnly={setFreeModelsOnly}
            consent={aiConsent}
            setConsent={setAiConsent}
            aiStatus={aiStatus}
            aiError={aiError}
            aiResult={aiResult}
            runReview={runAiReview}
            cancelReview={cancelAiReview}
            copyReview={copyAiReview}
            backendStatus={backendStatus}
            aiQuestion={aiQuestion}
            setAiQuestion={setAiQuestion}
            conversation={aiConversation}
            followUpStatus={followUpStatus}
            followUpError={followUpError}
            runFollowUp={runAiFollowUp}
            loadDemo={loadDemo}
          />
        ) : null}

        {activeTab === "history" ? (
          <HistoryPage
            container={container}
            analysis={analysis}
            backendStatus={backendStatus}
            backendFeatures={backendFeatures}
            history={history}
            historyStatus={historyStatus}
            historyError={historyError}
            saveCurrent={saveCurrentAudit}
            refreshHistory={refreshHistory}
            deleteSaved={deleteSavedAudit}
            loadDemo={loadDemo}
          />
        ) : null}

        {activeTab === "report" ? (
          <ReportPage
            container={container}
            analysis={analysis}
            sourceName={sourceName}
            aiResult={aiResult}
            governance={governance}
            comparison={comparison}
            exportReport={runExport}
            switchTab={switchTab}
            loadDemo={loadDemo}
          />
        ) : null}

        <footer>
          <span>
            GTM Audit Atlas · local deterministic core · Cloudflare Worker {backendStatus}
          </span>
          <span>{currentFindings.length} current findings</span>
        </footer>
      </main>

      <div className={`toast ${toast ? "show" : ""}`} role="status" aria-live="polite">
        {toast}
      </div>
    </div>
  );
}

function OverviewPage({
  container,
  analysis,
  sourceName,
  jsonText,
  setJsonText,
  showPaste,
  setShowPaste,
  importError,
  importContainer,
  handleFile,
  loadDemo,
  dragging,
  setDragging,
  fileInput,
  switchTab,
  backendStatus,
}) {
  const findings = analysis?.findings || [];
  const counts = countSeverities(findings);
  return (
    <section className="page">
      <PageHeading
        eyebrow="Audit / Overview"
        title="Container intelligence, without the guesswork"
        description="Upload one GTM export to infer naming conventions, map structural risks, validate GA4 events, and generate an audit rating."
        actions={
          <div className="page-action-stack">
            <StatusChip tone="success" icon={Shield}>
              JSON stays in this browser
            </StatusChip>
            <StatusChip tone={backendStatus === "online" ? "success" : "warning"}>
              Worker {backendStatus}
            </StatusChip>
          </div>
        }
      />

      <div className="metrics-grid">
        <MetricCard value={analysis?.counts.tags ?? 0} label="Tags" />
        <MetricCard value={analysis?.counts.triggers ?? 0} label="Triggers" />
        <MetricCard value={analysis?.counts.variables ?? 0} label="Variables" />
        <MetricCard value={counts.error} label="Errors" tone="error" />
        <MetricCard value={analysis?.ratings.overall.score ?? "—"} label="Overall rating" />
      </div>

      <div className="overview-grid">
        <article className="card upload-card">
          <CardHeader
            step="01"
            title={container ? "Replace container" : "Import GTM container"}
            description={
              container
                ? `${sourceName} is loaded. A new file will automatically re-run every audit.`
                : "Upload a Google Tag Manager container export or paste the raw JSON."
            }
            action={
              container ? (
                <StatusChip tone="success" icon={Check}>
                  Parsed
                </StatusChip>
              ) : (
                <StatusChip>Awaiting JSON</StatusChip>
              )
            }
          />

          <div
            className={`drop-zone ${dragging ? "dragging" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => fileInput.current?.click()}
            onKeyDown={(event) => {
              if (["Enter", " "].includes(event.key)) fileInput.current?.click();
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              handleFile(event.dataTransfer.files?.[0]);
            }}
          >
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              onChange={(event) => handleFile(event.target.files?.[0])}
              tabIndex={-1}
            />
            <span>
              <UploadCloud aria-hidden="true" />
            </span>
            <strong>Drop GTM export JSON here</strong>
            <small>or click to browse · maximum 15 MB</small>
          </div>

          <div className="upload-actions">
            <button className="text-button" type="button" onClick={() => setShowPaste((value) => !value)}>
              <Code2 aria-hidden="true" />
              {showPaste ? "Hide JSON paste" : "Paste JSON instead"}
            </button>
            <button className="secondary-button" type="button" onClick={loadDemo}>
              <Braces aria-hidden="true" />
              Use demo
            </button>
          </div>

          {showPaste ? (
            <div className="paste-panel">
              <label htmlFor="container-json">Container export JSON</label>
              <textarea
                id="container-json"
                rows={9}
                value={jsonText}
                onChange={(event) => setJsonText(event.target.value)}
                spellCheck={false}
                placeholder='{ "exportFormatVersion": 2, "containerVersion": { ... } }'
              />
              <button
                className="primary-button"
                type="button"
                onClick={() => importContainer(jsonText, "pasted-container.json")}
              >
                <FileSearch aria-hidden="true" />
                Parse and audit
              </button>
            </div>
          ) : null}
          {importError ? <p className="field-error">{importError}</p> : null}
        </article>

        <aside className="card intelligence-card">
          <CardHeader
            step="02"
            title="Automatic intelligence"
            description="Deterministic analysis runs immediately after a valid import."
          />
          <ol className="workflow-list">
            {[
              ["Parse once", "One shared tags, triggers, and variables model"],
              ["Infer convention", "Prefix vocabulary, separator, casing, confidence"],
              ["Audit structure", "References, consent, duplicates, lifecycle"],
              ["Rate quality", "Health, naming, measurement, and overall grades"],
            ].map(([title, description], index) => (
              <li key={title} className={container ? "complete" : index === 0 ? "active" : ""}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <strong>{title}</strong>
                  <small>{description}</small>
                </div>
              </li>
            ))}
          </ol>
          <div className="privacy-callout">
            <ShieldCheck aria-hidden="true" />
            <div>
              <strong>AI is never required</strong>
              <p>Ratings and findings are generated locally. An AI provider is contacted only when requested.</p>
            </div>
          </div>
        </aside>
      </div>

      {analysis ? (
        <>
          <div className="rating-grid">
            <RatingCard title="Overall" rating={analysis.ratings.overall} />
            <RatingCard title="Health" rating={analysis.ratings.health} compact />
            <RatingCard title="Naming" rating={analysis.ratings.naming} compact />
            <RatingCard title="Measurement" rating={analysis.ratings.measurement} compact />
          </div>

          <article className="card findings-card">
            <CardHeader
              step="03"
              title="Priority findings"
              description="The highest-severity results across health, naming, and event validation."
              action={
                <button className="text-button" type="button" onClick={() => switchTab("report")}>
                  View all <ChevronRight aria-hidden="true" />
                </button>
              }
            />
            <FindingsTable findings={findings.slice(0, 16)} showSection />
          </article>
        </>
      ) : null}
    </section>
  );
}

function NamingPage({
  container,
  analysis,
  draft,
  setDraft,
  includePaused,
  setIncludePaused,
  applyRules,
  restoreRules,
  loadDemo,
}) {
  if (!container || !analysis || !draft) {
    return (
      <section className="page">
        <PageHeading
          eyebrow="Governance / Naming"
          title="Automatic naming convention analysis"
          description="The app will infer rules as soon as a GTM export is loaded."
        />
        <WelcomeEmpty onLoadDemo={loadDemo} />
      </section>
    );
  }

  const findings = analysis.findings.filter((item) => item.tabId === "naming");
  return (
    <section className="page">
      <PageHeading
        eyebrow="Governance / Naming"
        title="Automatic naming convention analysis"
        description="The dominant prefix vocabulary, separator, and casing are inferred from the container and applied immediately."
        actions={<RatingCard title="Naming rating" rating={analysis.ratings.naming} compact />}
      />

      <div className="inference-banner">
        <WandSparkles aria-hidden="true" />
        <div>
          <strong>Rules inferred from {analysis.inventory.length} GTM objects</strong>
          <p>
            Rare exceptions are flagged instead of being silently absorbed into the convention.
            Review or edit the inferred policy below.
          </p>
        </div>
      </div>

      <div className="rule-cards">
        {["tag", "trigger", "variable"].map((type, index) => (
          <NamingRuleCard
            key={type}
            type={type}
            step={String(index + 1).padStart(2, "0")}
            rule={draft[type]}
            setRule={(next) => setDraft((current) => ({ ...current, [type]: next }))}
          />
        ))}
      </div>

      <div className="rule-actions card">
        <label className="toggle">
          <input
            type="checkbox"
            checked={includePaused}
            onChange={(event) => setIncludePaused(event.target.checked)}
          />
          <span aria-hidden="true" />
          Include paused tags
        </label>
        <div>
          <button className="secondary-button" type="button" onClick={restoreRules}>
            <RefreshCw aria-hidden="true" />
            Restore inference
          </button>
          <button className="primary-button" type="button" onClick={applyRules}>
            <Check aria-hidden="true" />
            Apply and re-rate
          </button>
        </div>
      </div>

      <article className="card findings-card">
        <CardHeader
          step="04"
          title="Naming findings"
          description="Exceptions, weak structure, casing drift, placeholders, and near duplicates."
          action={<StatusChip>{findings.length} findings</StatusChip>}
        />
        <FindingsTable findings={findings} />
      </article>
    </section>
  );
}

function NamingRuleCard({ type, step, rule, setRule }) {
  const label = `${type[0].toUpperCase()}${type.slice(1)}s`;
  return (
    <article className="card rule-card">
      <CardHeader
        step={step}
        title={label}
        description={`${rule.sampleSize} names analyzed`}
        action={<StatusChip tone={rule.confidence >= 70 ? "success" : "warning"}>{rule.confidence}% confidence</StatusChip>}
      />
      <div className="confidence-track" aria-label={`${rule.confidence}% inference confidence`}>
        <span style={{ width: `${rule.confidence}%` }} />
      </div>
      <label>
        Accepted prefixes
        <input
          value={rule.prefixes.join(", ")}
          onChange={(event) =>
            setRule({
              ...rule,
              prefixes: event.target.value.split(","),
              source: "reviewed",
            })
          }
          placeholder="GA4, META, HTML"
        />
        <small>Comma-separated prefix vocabulary</small>
      </label>
      <div className="two-fields">
        <label>
          Separator
          <select
            value={rule.separator}
            onChange={(event) => setRule({ ...rule, separator: event.target.value, source: "reviewed" })}
          >
            <option value=" - ">Space – dash – space</option>
            <option value=" — ">Space – em dash – space</option>
            <option value=" | ">Space – pipe – space</option>
            <option value=": ">Colon + space</option>
            <option value="_">Underscore</option>
            <option value="-">Hyphen</option>
            <option value=" ">Space</option>
          </select>
        </label>
        <label>
          Remainder casing
          <select
            value={rule.casing}
            onChange={(event) => setRule({ ...rule, casing: event.target.value, source: "reviewed" })}
          >
            <option value="title">Title Case</option>
            <option value="snake">snake_case</option>
            <option value="kebab">kebab-case</option>
            <option value="lower">lower case</option>
            <option value="upper">UPPER CASE</option>
            <option value="mixed">Do not enforce</option>
          </select>
        </label>
      </div>
      <dl className="rule-evidence">
        <div>
          <dt>Parsed</dt>
          <dd>{rule.parsedRatio}%</dd>
        </div>
        <div>
          <dt>Separator consensus</dt>
          <dd>{rule.separatorConsensus}%</dd>
        </div>
        <div>
          <dt>Casing consensus</dt>
          <dd>{rule.casingConsensus}%</dd>
        </div>
      </dl>
      <p className="rule-summary">
        Expected: <b>{rule.prefixes[0] || "PREFIX"}</b>
        {displaySeparator(rule.separator)} <b>{casingLabel(rule.casing)}</b>
      </p>
    </article>
  );
}

function EventsPage({
  container,
  analysis,
  manualText,
  setManualText,
  manualEvents,
  manualError,
  validateManualEvents,
  clearManualEvents,
  useManualDemo,
  loadDemo,
}) {
  if (!container || !analysis) {
    return (
      <section className="page">
        <PageHeading
          eyebrow="GA4 / Measurement"
          title="Event parameter validation"
          description="Auto-pull GA4 Event tags and validate pre-launch dataLayer payloads."
        />
        <WelcomeEmpty onLoadDemo={loadDemo} />
      </section>
    );
  }

  const findings = analysis.findings.filter((item) => item.tabId === "events");
  return (
    <section className="page">
      <PageHeading
        eyebrow="GA4 / Measurement"
        title="Event parameter validation"
        description="Recommended-event schemas, parameter types, event names, and nested ecommerce items."
        actions={<RatingCard title="Measurement" rating={analysis.ratings.measurement} compact />}
      />

      <div className="event-grid">
        <article className="card">
          <CardHeader
            step="01"
            title="Auto-pulled GA4 Event tags"
            description="Derived from tags where GTM type is gaawe."
            action={<StatusChip tone="success">{analysis.autoEvents.length} found</StatusChip>}
          />
          <div className="event-list">
            {analysis.autoEvents.length ? (
              analysis.autoEvents.map((event, index) => (
                <div className="event-row" key={`${event.item}-${index}`}>
                  <span className="event-index">{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <strong>{event.item}</strong>
                    <small>{Object.keys(event.params).length} statically extracted parameters</small>
                  </div>
                  <code>{event.eventName}</code>
                  {event.paused ? <StatusChip tone="warning">Paused</StatusChip> : null}
                </div>
              ))
            ) : (
              <div className="small-empty">
                <Network aria-hidden="true" />
                No GA4 Event tags were found.
              </div>
            )}
          </div>
        </article>

        <aside className="card schema-overview">
          <CardHeader
            step="02"
            title="Validation scope"
            description="Deterministic checks used in this workspace."
          />
          <div className="schema-stat">
            <strong>19</strong>
            <span>recommended event schemas</span>
          </div>
          <ul>
            <li><Check aria-hidden="true" /> Required event parameters</li>
            <li><Check aria-hidden="true" /> Number, string, array, and currency types</li>
            <li><Check aria-hidden="true" /> Nested items[] identity, price, and quantity</li>
            <li><Check aria-hidden="true" /> Event-name format and duplicate implementation</li>
          </ul>
        </aside>
      </div>

      <article className="card manual-event-card">
        <CardHeader
          step="03"
          title="Manual dataLayer QA"
          description="Paste one event, an array of pushes, or an object with a dataLayer array."
          action={
            <StatusChip tone={manualEvents.length ? "success" : "neutral"}>
              {manualEvents.length ? `${manualEvents.length} validated` : "Optional"}
            </StatusChip>
          }
        />
        <label htmlFor="datalayer-json">Raw dataLayer JSON</label>
        <textarea
          id="datalayer-json"
          rows={9}
          value={manualText}
          onChange={(event) => setManualText(event.target.value)}
          spellCheck={false}
          placeholder='[{ "event": "purchase", "ecommerce": { "transaction_id": "T-1001", "value": 99.95, "currency": "USD", "items": [] } }]'
        />
        {manualError ? <p className="field-error">{manualError}</p> : null}
        <div className="button-row">
          <button className="primary-button" type="button" onClick={validateManualEvents}>
            <FileSearch aria-hidden="true" />
            Validate payload
          </button>
          <button className="secondary-button" type="button" onClick={useManualDemo}>
            Use purchase example
          </button>
          <button className="text-button" type="button" onClick={clearManualEvents}>
            Clear
          </button>
        </div>
      </article>

      <article className="card findings-card">
        <CardHeader
          step="04"
          title="Event findings"
          description="Combined results from GTM Event tags and manual dataLayer QA."
          action={<StatusChip>{findings.length} findings</StatusChip>}
        />
        <FindingsTable findings={findings} />
      </article>
    </section>
  );
}

function ExplorerPage({
  container,
  analysis,
  items,
  query,
  setQuery,
  type,
  setType,
  status,
  setStatus,
  loadDemo,
}) {
  if (!container || !analysis) {
    return (
      <section className="page">
        <PageHeading
          eyebrow="Inventory / Explorer"
          title="Search the parsed container"
          description="Inspect the shared tags, triggers, and variables inventory."
        />
        <WelcomeEmpty onLoadDemo={loadDemo} />
      </section>
    );
  }
  return (
    <section className="page">
      <PageHeading
        eyebrow="Inventory / Explorer"
        title="Search the parsed container"
        description="A fast, read-only view of every object extracted during the single import parse."
        actions={<StatusChip>{analysis.inventory.length} objects</StatusChip>}
      />
      <article className="card explorer-card">
        <div className="explorer-toolbar">
          <label className="search-field large">
            <Search aria-hidden="true" />
            <span className="sr-only">Search GTM objects</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, type, or ID…"
            />
          </label>
          <label>
            <span className="sr-only">Object type</span>
            <select value={type} onChange={(event) => setType(event.target.value)}>
              <option value="all">All objects</option>
              <option value="tag">Tags</option>
              <option value="trigger">Triggers</option>
              <option value="variable">Variables</option>
            </select>
          </label>
          <label>
            <span className="sr-only">Object status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </label>
          <span className="result-count">{items.length} results</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Object</th>
                <th>Name</th>
                <th>GTM type</th>
                <th>Status</th>
                <th>ID</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={`${item.objectType}-${item.id}-${index}`}>
                  <td><span className="object-type">{item.objectType}</span></td>
                  <td className="item-cell">{item.name}</td>
                  <td><code>{item.subtype}</code></td>
                  <td><StatusChip tone={item.status === "Paused" ? "warning" : "success"}>{item.status}</StatusChip></td>
                  <td><code>{item.id || "—"}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </section>
  );
}

function AiPage({
  container,
  analysis,
  provider,
  providerConfig,
  providers,
  setProvider,
  apiKey,
  setApiKey,
  showKey,
  setShowKey,
  rememberKey,
  setRememberKey,
  keyStatus,
  keyMeta,
  keyError,
  connect,
  disconnect,
  refreshModels,
  models,
  model,
  setModel,
  freeModelsOnly,
  setFreeModelsOnly,
  consent,
  setConsent,
  aiStatus,
  aiError,
  aiResult,
  runReview,
  cancelReview,
  copyReview,
  backendStatus,
  aiQuestion,
  setAiQuestion,
  conversation,
  followUpStatus,
  followUpError,
  runFollowUp,
  loadDemo,
}) {
  const visibleModels =
    providerConfig.supportsFreeModels && freeModelsOnly
      ? models.filter((item) => item.isFree)
      : models;

  function toggleFreeModels(checked) {
    setFreeModelsOnly(checked);
    if (checked) {
      const freeModel = models.find((item) => item.isFree);
      if (freeModel && !models.find((item) => item.id === model)?.isFree) {
        setModel(freeModel.id);
      }
    }
  }

  return (
    <section className="page">
      <PageHeading
        eyebrow="Optional / Multi-provider BYOK"
        title="AI-assisted governance review"
        description="Choose OpenRouter, OpenAI, Gemini, or Claude. The Worker normalizes every provider into one structured review and follow-up workflow."
        actions={
          <div className="page-action-stack">
            <StatusChip tone={keyStatus === "connected" ? "success" : "neutral"} icon={KeyRound}>
              {keyStatus === "connected" ? "Key connected" : "BYOK"}
            </StatusChip>
            <StatusChip tone={backendStatus === "online" ? "success" : "warning"}>
              Worker {backendStatus}
            </StatusChip>
          </div>
        }
      />

      <article className="card provider-card">
        <CardHeader
          step="01"
          title="Choose an AI provider"
          description="Each provider uses its own key and live model catalog. Switching providers clears the current AI result, not the local GTM audit."
        />
        <div className="provider-grid" role="radiogroup" aria-label="AI provider">
          {providers.map((item) => (
            <button
              className={`provider-option ${provider === item.id ? "active" : ""}`}
              type="button"
              role="radio"
              aria-checked={provider === item.id}
              key={item.id}
              onClick={() => setProvider(item.id)}
            >
              <span>{item.shortName.slice(0, 2).toUpperCase()}</span>
              <strong>{item.name}</strong>
              <small>{item.description}</small>
              {item.supportsFreeModels ? <b>Free catalog</b> : <b>Direct API</b>}
            </button>
          ))}
        </div>
      </article>

      <div className="ai-layout">
        <article className="card api-card">
          <CardHeader
            step="02"
            title={`Connect ${providerConfig.name}`}
            description="The key is sent only for the current request, is never written to D1, and is never included in reports."
            action={
              keyStatus === "connected" ? (
                <StatusChip tone="success" icon={Check}>Connected</StatusChip>
              ) : null
            }
          />
          <label htmlFor="provider-api-key">{providerConfig.name} API key</label>
          <div className="secret-field">
            <KeyRound aria-hidden="true" />
            <input
              id="provider-api-key"
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder={providerConfig.keyPlaceholder}
              disabled={keyStatus === "connected"}
            />
            <button type="button" onClick={() => setShowKey((value) => !value)} aria-label={showKey ? "Hide API key" : "Show API key"}>
              {showKey ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
          <label className="check-label">
            <input
              type="checkbox"
              checked={rememberKey}
              onChange={(event) => setRememberKey(event.target.checked)}
              disabled={keyStatus === "connected"}
            />
            Remember until this browser tab is closed
          </label>
          {keyError ? <p className="field-error">{keyError}</p> : null}
          <div className="button-row">
            {keyStatus === "connected" ? (
              <button className="secondary-button" type="button" onClick={disconnect}>
                <X aria-hidden="true" />
                Remove key
              </button>
            ) : (
              <button className="primary-button" type="button" onClick={connect} disabled={keyStatus === "loading"}>
                {keyStatus === "loading" ? <LoaderCircle className="spin" aria-hidden="true" /> : <KeyRound aria-hidden="true" />}
                Validate key
              </button>
            )}
            <a className="text-link" href={providerConfig.keyUrl} target="_blank" rel="noreferrer">
              Get a {providerConfig.shortName} key ↗
            </a>
          </div>
          {keyMeta ? (
            <dl className="key-meta">
              <div><dt>Label</dt><dd>{keyMeta.label || "Connected key"}</dd></div>
              <div><dt>Provider</dt><dd>{providerConfig.name}</dd></div>
              <div><dt>Models</dt><dd>{keyMeta.modelCount ?? models.length ?? "Loading"}</dd></div>
              <div><dt>Account</dt><dd>{keyMeta.is_free_tier ? "Free tier" : keyMeta.limit === null ? "No key limit" : "Connected"}</dd></div>
            </dl>
          ) : null}
        </article>

        <aside className="card data-boundary-card">
          <CardHeader
            step="03"
            title="Data boundary"
            description="Exactly what the optional AI request contains."
          />
          <ul>
            <li><Check aria-hidden="true" /><span><strong>Included</strong> Object names, types, inferred rules, ratings, and up to 80 findings</span></li>
            <li><X aria-hidden="true" /><span><strong>Excluded</strong> Raw JSON, parameter values, custom HTML, account IDs, and trigger IDs</span></li>
            <li><ShieldCheck aria-hidden="true" /><span><strong>Sanitized</strong> Obvious emails, keys, URLs, and long numeric identifiers are redacted</span></li>
          </ul>
          <p>
            {provider === "openrouter"
              ? "OpenRouter and the routed model provider receive the sanitized request."
              : `${providerConfig.name} receives the sanitized request directly through the Worker.`}
            {" "}Review the provider's privacy and data-retention terms before proceeding.
          </p>
        </aside>
      </div>

      <article className="card ai-run-card">
        <CardHeader
          step="04"
          title="Run governance review"
          description="Structured output keeps the response predictable and exportable."
          action={analysis ? <StatusChip tone="success">{analysis.findings.length} findings ready</StatusChip> : <StatusChip>No audit loaded</StatusChip>}
        />
        {!container || !analysis ? <WelcomeEmpty onLoadDemo={loadDemo} /> : (
          <>
            <div className="model-row">
              <label>
                {providerConfig.shortName} model
                <input
                  list={`${provider}-models`}
                  value={model}
                  onChange={(event) => setModel(event.target.value)}
                  placeholder={providerConfig.defaultModel}
                />
                <datalist id={`${provider}-models`}>
                  {visibleModels.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}{item.isFree ? " · Free" : ""}
                    </option>
                  ))}
                </datalist>
                <small>
                  {models.length
                    ? `${visibleModels.length} compatible model${visibleModels.length === 1 ? "" : "s"} shown from the live catalog.`
                    : "Connect the key to load compatible models, or enter an exact API model ID."}
                </small>
                <span className="model-controls">
                  {providerConfig.supportsFreeModels ? (
                    <label className="check-label">
                      <input
                        type="checkbox"
                        checked={freeModelsOnly}
                        onChange={(event) => toggleFreeModels(event.target.checked)}
                      />
                      Free models only
                    </label>
                  ) : (
                    <small>Direct provider usage may be billed. No model is marked free without live zero-price metadata.</small>
                  )}
                  {keyStatus === "connected" ? (
                    <button className="text-button" type="button" onClick={refreshModels}>
                      <RefreshCw aria-hidden="true" /> Refresh models
                    </button>
                  ) : null}
                </span>
              </label>
              <label className="consent-box">
                <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
                <span>
                  I understand that sanitized audit metadata will be sent to {providerConfig.name}
                  {provider === "openrouter" ? " and its routed model provider" : ""}.
                </span>
              </label>
            </div>
            {aiError ? <p className="field-error">{aiError}</p> : null}
            <div className="button-row">
              {aiStatus === "loading" ? (
                <button className="secondary-button" type="button" onClick={cancelReview}>
                  <X aria-hidden="true" />
                  Cancel review
                </button>
              ) : (
                <button className="primary-button" type="button" onClick={runReview} disabled={keyStatus !== "connected" || !consent}>
                  <Sparkles aria-hidden="true" />
                  Run AI review
                </button>
              )}
              {aiStatus === "loading" ? <span className="loading-label"><LoaderCircle className="spin" aria-hidden="true" /> Analyzing sanitized audit data…</span> : null}
            </div>
          </>
        )}
      </article>

      {aiResult?.review ? <AiReviewResult result={aiResult} onCopy={copyReview} /> : null}
      {aiResult?.review ? (
        <AiFollowUpPanel
          question={aiQuestion}
          setQuestion={setAiQuestion}
          conversation={conversation}
          status={followUpStatus}
          error={followUpError}
          onSubmit={runFollowUp}
          workerReady={backendStatus === "online"}
        />
      ) : null}
    </section>
  );
}

function AiReviewResult({ result, onCopy }) {
  const review = result.review;
  return (
    <article className="card ai-result-card">
      <CardHeader
        step="06"
        title="AI governance review"
        description={`${getProvider(result.provider).name} · Model: ${result.model}${result.usage?.total_tokens ? ` · ${result.usage.total_tokens} tokens` : ""}`}
        action={<button className="secondary-button" type="button" onClick={onCopy}><Clipboard aria-hidden="true" /> Copy JSON</button>}
      />
      <div className="ai-summary">
        <span><Sparkles aria-hidden="true" /></span>
        <div>
          <p className="eyebrow">Executive summary</p>
          <p>{review.executive_summary}</p>
        </div>
      </div>
      <div className="ai-score-grid">
        <div>
          <span>Advisory naming score</span>
          <strong>{review.naming_assessment.suggested_score}<small>/100</small></strong>
        </div>
        <div>
          <span>AI confidence</span>
          <strong>{review.naming_assessment.confidence}<small>%</small></strong>
        </div>
        <p>{review.naming_assessment.observed_pattern}</p>
      </div>
      <div className="ai-columns">
        <div>
          <h3>Strengths</h3>
          <ul>{review.naming_assessment.strengths.map((item) => <li key={item}><Check aria-hidden="true" />{item}</li>)}</ul>
        </div>
        <div>
          <h3>Risks</h3>
          <ul>{review.naming_assessment.risks.map((item) => <li key={item}><CircleAlert aria-hidden="true" />{item}</li>)}</ul>
        </div>
      </div>
      <h3>Priority actions</h3>
      <div className="action-list">
        {review.priority_actions.map((item, index) => (
          <div key={`${item.title}-${index}`} className={`action-item ${item.priority}`}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div><strong>{item.title}</strong><p>{item.why}</p><small>{item.fix}</small></div>
            <b>{item.priority}</b>
          </div>
        ))}
      </div>
      {review.naming_anomalies.length ? (
        <>
          <h3>Naming suggestions</h3>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Object</th><th>Current name</th><th>Concern</th><th>Suggested name</th></tr></thead>
              <tbody>
                {review.naming_anomalies.map((item, index) => (
                  <tr key={`${item.current_name}-${index}`}>
                    <td>{item.object_type}</td><td className="item-cell">{item.current_name}</td><td>{item.concern}</td><td className="item-cell">{item.suggested_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </article>
  );
}

function AiFollowUpPanel({
  question,
  setQuestion,
  conversation,
  status,
  error,
  onSubmit,
  workerReady,
}) {
  return (
    <article className="card ai-chat-card">
      <CardHeader
        step="05"
        title="Ask about this audit"
        description="Follow-up answers are constrained to the same sanitized audit evidence."
        action={
          <StatusChip tone={workerReady ? "success" : "warning"}>
            {workerReady ? "Worker ready" : "Worker required"}
          </StatusChip>
        }
      />
      {conversation.length ? (
        <div className="ai-conversation">
          {conversation.map((item, index) => (
            <div className={`chat-message ${item.role}`} key={`${item.role}-${index}`}>
              <span>{item.role === "assistant" ? "AI" : "You"}</span>
              <div>
                <p>{item.content}</p>
                {item.detail?.evidence?.length ? (
                  <ul>
                    {item.detail.evidence.map((evidence) => <li key={evidence}>{evidence}</li>)}
                  </ul>
                ) : null}
                {item.detail?.confidence !== undefined ? (
                  <small>{item.detail.confidence}% answer confidence</small>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <label htmlFor="audit-question">Question</label>
      <textarea
        id="audit-question"
        rows={3}
        value={question}
        maxLength={700}
        onChange={(event) => setQuestion(event.target.value)}
        placeholder="Which three changes should be fixed before the next GTM publish?"
      />
      {error ? <p className="field-error">{error}</p> : null}
      <div className="button-row">
        <button
          className="primary-button"
          type="button"
          onClick={onSubmit}
          disabled={!workerReady || !question.trim() || status === "loading"}
        >
          {status === "loading" ? <LoaderCircle className="spin" aria-hidden="true" /> : <Sparkles aria-hidden="true" />}
          {status === "loading" ? "Reviewing…" : "Ask AI"}
        </button>
        <span className="result-count">{question.length}/700</span>
      </div>
    </article>
  );
}

function ReportPage({
  container,
  analysis,
  sourceName,
  aiResult,
  governance,
  comparison,
  exportReport,
  switchTab,
  loadDemo,
}) {
  if (!container || !analysis) {
    return (
      <section className="page">
        <PageHeading
          eyebrow="Findings / Export"
          title="Consolidated audit report"
          description="Review ratings and export client-ready deliverables."
        />
        <WelcomeEmpty onLoadDemo={loadDemo} />
      </section>
    );
  }
  return (
    <section className="page">
      <PageHeading
        eyebrow="Findings / Export"
        title="Consolidated audit report"
        description="Deterministic findings, ratings, inferred conventions, and the optional AI review in one place."
        actions={
          <div className="export-buttons">
            <button className="secondary-button" type="button" onClick={() => exportReport("csv")}><TableProperties aria-hidden="true" /> CSV</button>
            <button className="secondary-button" type="button" onClick={() => exportReport("json")}><FileJson aria-hidden="true" /> JSON</button>
            <button className="primary-button" type="button" onClick={() => exportReport("docx")}><Download aria-hidden="true" /> Word report</button>
          </div>
        }
      />

      <div className="report-hero">
        <RatingCard title="Overall audit" rating={analysis.ratings.overall} />
        <div className="report-meta">
          <div><span>Source</span><strong>{sourceName}</strong></div>
          <div><span>Container</span><strong>{container.metadata.name}</strong></div>
          <div><span>Objects</span><strong>{analysis.inventory.length}</strong></div>
          <div><span>Policy gates</span><strong>{governance ? `${governance.passed}/${governance.total} passed` : "—"}</strong></div>
          <div><span>Baseline</span><strong>{comparison ? `${comparison.changeCount} changes` : "Not loaded"}</strong></div>
          <div><span>AI review</span><strong>{aiResult ? "Included" : "Not run"}</strong></div>
        </div>
      </div>

      <div className="rating-grid">
        <RatingCard title="Health" rating={analysis.ratings.health} compact />
        <RatingCard title="Naming" rating={analysis.ratings.naming} compact />
        <RatingCard title="Measurement" rating={analysis.ratings.measurement} compact />
        <button className="ai-report-link" type="button" onClick={() => switchTab("ai")}>
          <Bot aria-hidden="true" />
          <span><strong>{aiResult ? "Review AI analysis" : "Add optional AI review"}</strong><small>Multi-provider BYOK</small></span>
          <ChevronRight aria-hidden="true" />
        </button>
      </div>

      <article className="card findings-card">
        <CardHeader
          step="01"
          title="All audit findings"
          description="Search and filter the shared finding collection before export."
          action={<StatusChip>{analysis.findings.length} total</StatusChip>}
        />
        <FindingsTable findings={analysis.findings} showSection />
      </article>
    </section>
  );
}

function cloneRules(rules) {
  return Object.fromEntries(
    Object.entries(rules).map(([type, rule]) => [
      type,
      { ...rule, prefixes: [...rule.prefixes] },
    ]),
  );
}

function loadSessionProviderKeys() {
  return Object.fromEntries(
    AI_PROVIDERS.map((provider) => [
      provider.id,
      safeSessionGet(providerSessionKey(provider.id)) || "",
    ]),
  );
}

function chooseProviderModel(models, current, providerConfig, freeOnly) {
  if (!models.length) return current || providerConfig.defaultModel;
  const allowed = providerConfig.supportsFreeModels && freeOnly
    ? models.filter((item) => item.isFree)
    : models;
  if (allowed.some((item) => item.id === current)) return current;
  if (allowed.some((item) => item.id === providerConfig.defaultModel)) {
    return providerConfig.defaultModel;
  }
  return allowed[0]?.id || models[0].id;
}

function safeLocalGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeLocalSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* optional */ }
}
function safeJsonLocalGet(key) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
}
function safeSessionGet(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeSessionSet(key, value) {
  try { sessionStorage.setItem(key, value); } catch { /* optional */ }
}
function safeSessionRemove(key) {
  try { sessionStorage.removeItem(key); } catch { /* optional */ }
}

function normalizePolicy(value) {
  const profile = ["standard", "strict", "exploratory"].includes(value?.profile)
    ? value.profile
    : "standard";
  return {
    profile,
    minimumOverallScore: clampScore(value?.minimumOverallScore, 75),
    minimumNamingScore: clampScore(value?.minimumNamingScore, 70),
    minimumConsentCoverage: clampScore(value?.minimumConsentCoverage, 80),
    maximumPausedTags: clampScore(value?.maximumPausedTags, 5),
    allowCustomHtml: value?.allowCustomHtml !== false,
    requireGa4EventTags: value?.requireGa4EventTags !== false,
  };
}

function clampScore(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.min(100, Math.round(number))) : fallback;
}

function getOrCreateWorkspaceToken() {
  const existing = safeLocalGet("gtm-atlas-workspace-token");
  if (/^[A-Za-z0-9_-]{24,160}$/.test(existing || "")) return existing;
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const token = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  safeLocalSet("gtm-atlas-workspace-token", token);
  return token;
}

export default App;
